// Full turnaround experience for a single order. Reached by tapping any order
// card. Reads from the dashboard cache (active orders) or the history cache
// (past orders) and renders the complete status: roll breakdown, turnaround
// estimate (color/B&W/final dates), queue status, special messages, and the
// relevant actions (download / pay / schedule pickup).
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useRouter } from "../lib/router";
import type { Dashboard, PastOrder, PendingOrder, ReadyOrder, UnpaidInvoice } from "../lib/types";
import { formatDate, queueLabel, rollBreakdown } from "../lib/format";
import { Card, Money, Spinner } from "./ui";

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-2 border-b border-line-soft last:border-0">
      <span className="text-ink-subtle text-[13px]">{label}</span>
      <span className="text-ink text-[13px] text-right">{value}</span>
    </div>
  );
}

export function OrderDetail({ orderId }: { orderId: string }) {
  const { navigate } = useRouter();
  const dash = useQuery({ queryKey: ["dashboard"], queryFn: () => api.get<Dashboard>("/api/dashboard"), staleTime: 60_000 });
  const hist = useQuery({ queryKey: ["orders", "history"], queryFn: () => api.get<{ orders: PastOrder[] }>("/api/orders/history"), staleTime: 60_000 });

  const pending = dash.data?.pendingOrders.find((o) => o.invoiceId === orderId);
  const ready = dash.data?.readyOrders.find((o) => o.invoiceId === orderId);
  const unpaid = dash.data?.unpaidInvoices.find((i) => i.id === orderId);
  const past = hist.data?.orders.find((o) => o.invoiceId === orderId);

  const loading = dash.isLoading || hist.isLoading;

  return (
    <div className="pb-6">
      <header className="flex items-center gap-2 px-3 h-14 sticky top-0 bg-canvas/95 backdrop-blur z-10">
        <button onClick={() => navigate("/")} aria-label="Back" className="h-9 w-9 rounded-full text-ink-muted active:bg-line-soft">
          <i className="ph ph-arrow-left text-lg" />
        </button>
        <h1 className="text-[17px] font-bold text-navy">Order details</h1>
      </header>

      {loading && <Spinner />}

      {!loading && !pending && !ready && !unpaid && !past && (
        <div className="px-4 text-center text-ink-subtle py-14">
          <i className="ph ph-file-dashed text-4xl text-ink-faint" />
          <p className="mt-2">We couldn't find that order.</p>
        </div>
      )}

      <div className="px-4 flex flex-col gap-4">
        {ready && <ReadyDetail o={ready} />}
        {pending && <PendingDetail o={pending} onSchedulePickup={() => navigate("/pickups")} />}
        {unpaid && !ready && <UnpaidDetail inv={unpaid} />}
        {past && !pending && !ready && !unpaid && <PastDetail o={past} />}
      </div>
    </div>
  );
}

function StatusPill({ tone, icon, children }: { tone: string; icon: string; children: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-[13px] font-medium px-3 py-1 rounded-full ${tone}`}>
      <i className={`ph-fill ${icon}`} /> {children}
    </span>
  );
}

function PendingDetail({ o, onSchedulePickup }: { o: PendingOrder; onSchedulePickup: () => void }) {
  const t = o.turnaround || {};
  const rolls = rollBreakdown(o);
  const finalEst = formatDate(t.estimatedFinalDate, { weekday: "long", month: "long", day: "numeric" });
  const colorEst = formatDate(t.estimatedColorDate);
  const bwEst = formatDate(t.estimatedBWDate);

  const qc = useQueryClient();
  const [bwAsk, setBwAsk] = useState(false);
  const priorityM = useMutation({
    mutationFn: (bwResponse?: string) => api.post("/api/orders/priority", { invoiceId: o.invoiceId, bwResponse }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dashboard"] }),
  });
  const deliveryM = useMutation({
    mutationFn: () => api.post("/api/orders/confirm-delivery", { confirmation: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dashboard"] }),
  });
  const canPriority = (t.priorityTier === "eligible" || t.priorityTier === "eligible-bw-check") && !t.priorityRequestSent;
  const needsBwCheck = t.priorityTier === "eligible-bw-check";

  return (
    <>
      <Card className="p-5 text-center">
        <StatusPill tone="bg-navy-soft text-navy" icon="ph-flask">
          {queueLabel(t.queueStatus)}
        </StatusPill>
        {finalEst && (
          <>
            <div className="text-ink-subtle text-[13px] mt-4">Estimated ready</div>
            <div className="text-navy font-bold text-2xl mt-0.5">{finalEst}</div>
          </>
        )}
        {t.isPastSLA && <div className="text-orange text-[13px] mt-2">This is taking longer than our usual 2–3 day turnaround.</div>}
      </Card>

      {t.stillWorkingToday === false && (
        <p className="text-[13px] text-ink-subtle text-center -mt-1">The lab is done processing for today.</p>
      )}

      {(t.specialMessages?.length ?? 0) > 0 && (
        <Card className="p-4 flex flex-col gap-2">
          {t.specialMessages!.map((m, i) => (
            <p key={i} className="text-[13px] text-ink-muted leading-snug">
              {m}
            </p>
          ))}
        </Card>
      )}

      {/* Queue position */}
      {(t.rollsAheadInQueue != null || t.workDaysSinceDropoff != null) && (
        <Card className="p-4">
          <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle mb-1">In the queue</h3>
          {t.rollsAheadInQueue != null && <Row label="Rolls ahead of you" value={t.rollsAheadInQueue} />}
          {t.pendingClubPriorityRolls != null && t.pendingClubPriorityRolls > 0 && (
            <Row label="Club-priority rolls ahead today" value={t.pendingClubPriorityRolls} />
          )}
          {typeof t.workDaysSinceDropoff === "number" && <Row label="Business days in the lab" value={t.workDaysSinceDropoff} />}
          {typeof t.workDaysRemaining === "number" && <Row label="Business days remaining" value={t.workDaysRemaining} />}
        </Card>
      )}

      <Card className="p-4">
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle mb-1">Order</h3>
        {o.orderType === "mail-in" && <Row label="Type" value="Mail-in" />}
        {rolls.map((r) => (
          <Row key={r} label="Rolls" value={r} />
        ))}
        {o.scanResolution && <Row label="Scans" value={o.scanResolution} />}
        {o.dropDate && <Row label="Dropped off" value={o.dropTime ? `${o.dropDate} at ${o.dropTime}` : o.dropDate} />}
        {colorEst && <Row label="Color ready" value={colorEst} />}
        {bwEst && <Row label="B&W ready" value={bwEst} />}
      </Card>

      {/* Mail-in: awaiting delivery -> confirm-delivery */}
      {t.mailInAwaitingDelivery && (
        <Card className="p-4">
          <p className="text-[13px] text-ink-muted mb-3">
            We haven't logged your package in yet. If your tracking shows it delivered, let us know and we'll go find it.
          </p>
          {deliveryM.isSuccess ? (
            <p className="text-sev-success text-[13px]">Thanks — we'll take a look.</p>
          ) : (
            <button
              onClick={() => deliveryM.mutate()}
              disabled={deliveryM.isPending}
              className="h-11 w-full rounded-xl bg-navy text-white font-medium disabled:opacity-50"
            >
              {deliveryM.isPending ? "Sending…" : "My tracking shows it delivered"}
            </button>
          )}
        </Card>
      )}

      {/* Overdue -> request priority ("let the lab know"), with B&W self-check */}
      {t.priorityRequestSent ? (
        <p className="text-[13px] text-orange text-center">Priority requested — we'll take a look.</p>
      ) : priorityM.isSuccess ? (
        <p className="text-[13px] text-sev-success text-center">Thanks — we've flagged this for the lab.</p>
      ) : canPriority ? (
        <Card className="p-4">
          {needsBwCheck && bwAsk ? (
            <>
              <p className="text-[13px] text-ink-muted mb-3">
                Quick check first — does this order include any black &amp; white film? (B&amp;W runs on a ~2-week batch,
                so it isn't overdue yet.)
              </p>
              <div className="flex gap-2">
                <button onClick={() => priorityM.mutate("possible-bw")} disabled={priorityM.isPending} className="flex-1 h-11 rounded-xl bg-navy-soft text-navy font-medium border border-navy-line">
                  It might
                </button>
                <button onClick={() => priorityM.mutate("sure-no-bw")} disabled={priorityM.isPending} className="flex-1 h-11 rounded-xl bg-navy text-white font-medium">
                  No, it's all color
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-[13px] text-ink-muted mb-3">This is taking longer than our usual turnaround. Want us to take a look?</p>
              <button
                onClick={() => (needsBwCheck ? setBwAsk(true) : priorityM.mutate(undefined))}
                disabled={priorityM.isPending}
                className="h-11 w-full rounded-xl bg-orange text-white font-medium disabled:opacity-50"
              >
                {priorityM.isPending ? "Sending…" : "Let the lab know it's overdue"}
              </button>
            </>
          )}
        </Card>
      ) : null}

      <button onClick={onSchedulePickup} className="h-12 rounded-xl bg-navy-soft text-navy font-medium border border-navy-line inline-flex items-center justify-center gap-2">
        <i className="ph ph-calendar-check" /> Schedule a pickup
      </button>
    </>
  );
}

function ReadyDetail({ o }: { o: ReadyOrder }) {
  const rolls = (o.colorRolls || 0) + (o.bwRolls || 0);
  return (
    <>
      <Card className="p-5 text-center">
        <StatusPill tone="bg-green-50 text-sev-success" icon="ph-check-circle">
          Ready
        </StatusPill>
        <p className="text-ink font-medium mt-3">{o.message || `${rolls} roll${rolls === 1 ? "" : "s"} scanned`}</p>
        {o.dropDate && <p className="text-ink-subtle text-[13px] mt-1">Dropped {o.dropDate}</p>}
      </Card>
      <div className="flex flex-col gap-2">
        {o.downloadUrl && (
          <a href={o.downloadUrl} className="h-12 rounded-xl bg-navy text-white font-medium inline-flex items-center justify-center gap-2">
            <i className="ph ph-download-simple" /> Download scans
          </a>
        )}
        {o.payUrl && (
          <a href={o.payUrl} className="h-12 rounded-xl bg-orange text-white font-medium inline-flex items-center justify-center gap-2">
            <i className="ph ph-credit-card" /> Pay {o.amount ? `$${o.amount}` : null}
          </a>
        )}
      </div>
    </>
  );
}

function UnpaidDetail({ inv }: { inv: UnpaidInvoice }) {
  return (
    <Card className="p-5 text-center">
      <StatusPill tone="bg-orange-soft text-orange" icon="ph-credit-card">
        Awaiting payment
      </StatusPill>
      <div className="text-navy font-bold text-2xl mt-3">${inv.amount}</div>
      {inv.description && <p className="text-ink-subtle text-[13px] mt-1">{inv.description}</p>}
      <a href={inv.hostedUrl} className="mt-4 h-12 rounded-xl bg-orange text-white font-medium inline-flex items-center justify-center gap-2 w-full">
        <i className="ph ph-credit-card" /> Pay now
      </a>
    </Card>
  );
}

function PastDetail({ o }: { o: PastOrder }) {
  const statusLabel = o.returnStatus
    ? o.returnStatus.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "Completed";
  const rolls = rollBreakdown(o);
  const dropped = formatDate(o.date, { month: "long", day: "numeric", year: "numeric" });
  const printsEstimate = (o.totalRolls * 16.56).toFixed(2);
  const printsM = useMutation({
    mutationFn: () => api.post("/api/orders/prints", { invoiceId: o.invoiceId, rollCount: o.totalRolls }),
  });
  return (
    <>
      <Card className="p-5 text-center">
        <StatusPill tone="bg-line-soft text-ink-muted" icon="ph-check">
          {statusLabel}
        </StatusPill>
        {dropped && <p className="text-ink-subtle text-[13px] mt-3">Dropped {dropped}</p>}
      </Card>
      <Card className="p-4">
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle mb-1">Order</h3>
        {rolls.map((r) => (
          <Row key={r} label="Rolls" value={r} />
        ))}
        {o.scanResolution && <Row label="Scans" value={o.scanResolution} />}
        {o.negativeReturn && <Row label="Negatives" value={o.negativeReturn} />}
        {o.prints && <Row label="Prints" value={o.prints} />}
        {o.twinCheck && <Row label="Twin check" value={o.twinCheck} />}
        {o.trackingNumber && <Row label="Tracking" value={o.trackingNumber} />}
      </Card>

      {o.canOrderPrints && (
        <Card className="p-4 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <i className="ph ph-images text-xl text-orange" />
            <div className="flex-1 text-[13px] text-ink-muted">
              Twin-check stickers are on file — you can still order prints of all {o.totalRolls} roll
              {o.totalRolls === 1 ? "" : "s"} from these negatives.
            </div>
          </div>
          {printsM.isSuccess ? (
            <p className="text-sev-success text-[13px]">
              Prints ordered — we've emailed you an invoice to pay. Whole rolls only, ~$0.46 / print.
            </p>
          ) : (
            <button
              onClick={() => {
                if (confirm(`Order prints for all ${o.totalRolls} rolls? We'll email you an invoice (about $${printsEstimate}).`))
                  printsM.mutate();
              }}
              disabled={printsM.isPending}
              className="h-11 rounded-xl bg-orange text-white font-medium disabled:opacity-50"
            >
              {printsM.isPending ? "Ordering…" : `Order prints · ~$${printsEstimate}`}
            </button>
          )}
          {printsM.isError && <p className="text-sev-error text-[13px]">Couldn't place the print order. Try again.</p>}
        </Card>
      )}
    </>
  );
}
